import React, { PureComponent } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';

import { Loader } from 'react-loaders';

import { batchRequest, getRequest, putRequest, postRequest } from '../../../../api/ApiService';
import { clearAppGeneralInfoCache, fetchAppGeneralInfo } from '../../../../api/AppGeneralInfoService';
import { changeLocation } from 'api/Location';
import { getDateInCurrentTimezone } from '../../../../services/utils';
import Auth from '../../../../routes/Auth';
import {
  Wrapper,
  Title,
  Text,
  DataContainer,
  DataContainerTitle,
  FourColumnsRow,
  DataColumn,
  DataTitle,
  ButtonRow,
  Button,
} from '../components/ui';
import InputPercentage from '../components/input-percentage';

import ErrorAlert from '../../../../components/alerts/error';
import SuccessAlert from '../../../../components/alerts/success';

// "Scoring Weights" page — consolidated home for:
//   1. Round Weights   (formerly under Team Score / Round Targets)
//      Per-round grading weight, summed across all rounds = 100%.
//      Drives how each round's score contributes to the cumulative
//      Team Score.
//   2. Score Weights   (formerly the only thing on this page)
//      How the four activity scores combine into an individual
//      score: team + peer + feedback submission + engagement.
//      Sum must equal 100%.
//
// Both sections share the alert + success surface at the top.
class OverallScore extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      // Score weights (gradingweights endpoint)
      teamScore: 0,
      peerScore: 0,
      feedbackSubmission: 0,
      engagementScore: 0,
      gradingWeightsID: 0,
      courseID: 0,
      scoreWeightsChanged: false,
      // Round weights: array of exactly 3 slots keyed by round number,
      // each carrying `source` ('round_api' | 'scheduler' | 'default')
      // and the id + fields needed to PUT back to the right endpoint.
      // Source is picked from the fallback chain:
      //   game.reset=1  →  round API → scheduler → default
      //   game.reset=0  →  scheduler → default
      roundsWeights: [],
      roundsWeightsValueChanged: false,
      isDefaultRoundsActive: false,
      gameType: null,
      // game.reset flag — toggles whether updates go to the round API
      // or to the scheduler endpoint.
      gameReset: false,
      // Shared alert surface
      showError: false,
      errorTitle: '',
      errorMessage: '',
      showSuccess: false,
      successMessage: '',
    };
  }

  componentDidMount() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const courseId = Number(Auth.getCourseId());
    const gameId = Number(Auth.getGameId());

    Promise.all([
      batchRequest([
        // Scope by course. Without ?course= the BE returns a paginated
        // list across every course this instructor touches, and
        // results[0] is whichever row happens to sort first (often a
        // foreign course). That's how gradingWeightsID ended up as 0
        // here: the fetch returned no row for the current course, we
        // fell back to the default 0, and the PUT went to
        // /gradingweights/0/. Defensive filter below matches the
        // TeamEvaluationSurvey pattern.
        `${apiUrl}v2/games/gradingweights/?course=${courseId}`,
        `${apiUrl}game/rounds/`,
        `${apiUrl}courses/${courseId}/`,
        `${apiUrl}v2/games/roundschedulers/`,
      ]),
      // general-info gives us the game.reset flag; the service is cached
      // and in-flight-deduped so this piggybacks on App.js's own call
      // without a second roundtrip when the cache is warm.
      fetchAppGeneralInfo(gameId, 'instructor').catch(() => ({})),
    ])
      .then(([res, generalInfo]) => {
        const gwBody = (res[0] && res[0].body) || {};
        const gwRaw = Array.isArray(gwBody) ? gwBody : (gwBody.results || []);
        const gwMatched = gwRaw.filter(r => Number(r.course) === Number(courseId));
        const gradingWeights = gwMatched[0] || null;
        const courseBody = (res[2] && res[2].body) || {};
        const rawGameType = courseBody.game_type || null;
        const gameType = rawGameType
          ? rawGameType.charAt(0).toUpperCase() + rawGameType.slice(1).toLowerCase()
          : null;

        let scoreWeightsState = {};
        if (gradingWeights) {
          scoreWeightsState = {
            teamScore: gradingWeights.team_grade
              ? Number(gradingWeights.team_grade * 100).toFixed(2)
              : '0.00',
            peerScore: gradingWeights.peer_weight
              ? Number(gradingWeights.peer_weight * 100).toFixed(2)
              : '0.00',
            feedbackSubmission: gradingWeights.feedback_submission
              ? Number(gradingWeights.feedback_submission * 100).toFixed(2)
              : '0.00',
            engagementScore: gradingWeights.engagement_score
              ? Number(gradingWeights.engagement_score * 100).toFixed(2)
              : '0.00',
            gradingWeightsID: gradingWeights.id,
            courseID: gradingWeights.course,
          };
        }

        const roundsFromApi = Array.isArray(res[1].body) ? res[1].body : [];
        const schedulersBody = res[3] && res[3].body;
        const schedulersRaw = Array.isArray(schedulersBody)
          ? schedulersBody
          : (schedulersBody && schedulersBody.results) || [];

        // game.reset lives on the game record returned by general-info.
        const games = (generalInfo && generalInfo.games) || [];
        const game = games.find(g => Number(g.id) === gameId) || games[0] || {};
        const gameReset = !!game.reset;

        // Build exactly 3 slots keyed by round number so the UI always
        // reads Round 1 | Round 2 | Round 3 left-to-right regardless
        // of the order the BE returned rows.
        const toDisplayWeight = (gw) => gw != null
          ? Number(Number(gw) * 100).toFixed(2)
          : '33.33';
        const roundsWeights = [1, 2, 3].map((number) => {
          const apiRound = roundsFromApi.find(
            r => Number(r.number) === number && r.practice !== 'Yes'
          );
          const scheduler = schedulersRaw.find((s) => {
            const sNumber = s.round_number !== undefined ? s.round_number : s.number;
            return Number(sNumber) === number && s.practice !== true;
          });

          if (gameReset) {
            if (apiRound) {
              return {
                number,
                source: 'round_api',
                id: apiRound.id,
                gradingWeight: toDisplayWeight(apiRound.grading_weight),
                startDate: apiRound.start_date,
                endDate: apiRound.end_date,
                game: apiRound.game,
              };
            }
            if (scheduler) {
              return {
                number,
                source: 'scheduler',
                id: scheduler.id,
                gradingWeight: toDisplayWeight(scheduler.grading_weight),
                startDate: scheduler.start_date,
                endDate: scheduler.end_date,
                game: null,
              };
            }
          } else if (scheduler) {
            return {
              number,
              source: 'scheduler',
              id: scheduler.id,
              gradingWeight: toDisplayWeight(scheduler.grading_weight),
              startDate: scheduler.start_date,
              endDate: scheduler.end_date,
              game: null,
            };
          }

          // No backing record exists for this round number yet.
          return {
            number,
            source: 'default',
            id: null,
            gradingWeight: '33.33',
            startDate: null,
            endDate: null,
            game: null,
          };
        });

        const isDefaultRoundsActive = roundsWeights.every(r => r.source === 'default');

        this.setState({
          loaded: true,
          ...scoreWeightsState,
          roundsWeights,
          isDefaultRoundsActive,
          gameType,
          gameReset,
          courseID: scoreWeightsState.courseID != null
            ? scoreWeightsState.courseID
            : courseId,
        });
      })
      .catch(() => {
        this.setState({ loaded: true });
      });
  }

  // ---- score weight handlers -------------------------------------

  handleScoreWeightChange = (name, value) => {
    this.setState({
      [name]: value,
      scoreWeightsChanged: true,
    });
  };

  submitScoreWeights = () => {
    const {
      teamScore,
      peerScore,
      feedbackSubmission,
      engagementScore,
      gradingWeightsID,
      courseID,
    } = this.state;

    const sum =
      Number(teamScore) +
      Number(peerScore) +
      Number(feedbackSubmission) +
      Number(engagementScore);

    if (sum !== 100) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Must equal 100%',
        errorMessage:
          "The sum of the score weights doesn't equal 100%. Adjust the values so they total 100%.",
      });
      return;
    }

    // No existing row for this course yet (typical when the
    // instructor lands on Grading before onboarding created the row).
    // POST to create instead of PUTting to /gradingweights/0/.
    const resolvedCourseId = Number(courseID) || Number(Auth.getCourseId());
    const payload = {
      team_grade: teamScore / 100,
      peer_weight: peerScore / 100,
      feedback_submission: feedbackSubmission / 100,
      engagement_score: engagementScore / 100,
      course: resolvedCourseId,
    };
    const apiUrl = process.env.REACT_APP_API_URL;
    const req = gradingWeightsID
      ? putRequest(`${apiUrl}v2/games/gradingweights/${gradingWeightsID}/`).send(payload)
      : postRequest(`${apiUrl}v2/games/gradingweights/`).send(payload);

    req
      .then((res) => {
        const newId = res && res.body && res.body.id;
        this.setState({
          scoreWeightsChanged: false,
          showSuccess: true,
          successMessage: 'The score weights are updated.',
          gradingWeightsID: newId || gradingWeightsID,
          courseID: resolvedCourseId,
        });
      })
      .catch(() => {});
  };

  // ---- round weight handlers -------------------------------------

  handleRoundWeightChange = (name, value) => {
    const roundsWeights = _.cloneDeep(this.state.roundsWeights);
    const index = roundsWeights.findIndex((i) => i.number === Number(name));
    if (index < 0) return;
    roundsWeights[index].gradingWeight = value;
    this.setState({
      roundsWeights,
      roundsWeightsValueChanged: true,
    });
  };

  submitRoundWeights = () => {
    const { roundsWeights, isDefaultRoundsActive, courseID } = this.state;

    if (isDefaultRoundsActive) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Rounds not set up',
        errorMessage: (
          <span>
            You'll need to activate rounds before you can set round weights.{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => changeLocation('/instructor/rounds')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  changeLocation('/instructor/rounds');
                }
              }}
              style={{
                color: '#5c91f5',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 600,
              }}
            >
              Set up Rounds
            </span>
            .
          </span>
        ),
      });
      return;
    }

    // Only rounds that have a backing record on the BE (round_api or
    // scheduler) can be saved. Rounds without a record (source =
    // 'default') are rendered as disabled inputs so they're already
    // excluded from edits — here we also skip them from the sum
    // validation and PUT list.
    const savableRounds = roundsWeights.filter(r => r.source !== 'default');
    if (savableRounds.length === 0) {
      // Shouldn't be reachable through the UI (the whole section is
      // hidden when every round is default-sourced) but guard anyway.
      return;
    }

    const sum = savableRounds.reduce(
      (partial, round) => partial + Number(round.gradingWeight),
      0,
    );
    // Tolerate ≤1% float / rounding slop so the 33.33 × 3 = 99.99
    // default still saves without forcing the instructor to tweak
    // one row to 33.34 by hand.
    if (Math.abs(sum - 100) > 1) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Must equal 100%',
        errorMessage:
          "The sum of all round weights doesn't equal 100%. Adjust the values so they total 100%.",
      });
      return;
    }

    // Target endpoint tracks the slot's source so the PUT always hits
    // the record the display is reading from:
    //   source='round_api' → PUT /game/rounds/<id>/
    //   source='scheduler' → PUT /v2/games/roundschedulers/<id>/
    // In the normal case this collapses to the spec: game.reset=0
    // slots are all scheduler-sourced; game.reset=1 slots are all
    // round-api-sourced. The source-based branch also covers the
    // edge case where game.reset=1 but a round API record is missing
    // for one of the rounds, in which case the display fell back to
    // the scheduler and we must update the scheduler, not 404 on a
    // non-existent round API record.
    //
    // Promise.all so success/error only flips after every PUT settles
    // (the old forEach was fire-and-forget and silently masked
    // partial failures). Also bust the general-info cache so the
    // Rounds page's next fetchAppGeneralInfo hits the wire and picks
    // up the new weights — Round Admin already does this after inline
    // edits.
    const apiUrl = process.env.REACT_APP_API_URL;
    const requests = savableRounds.map((round) => {
      if (round.source === 'round_api') {
        return putRequest(`${apiUrl}game/rounds/${round.id}/`)
          .send({
            grading_weight: Number(round.gradingWeight) / 100,
            number: round.number,
            game: round.game,
            end_date: round.endDate,
            start_date: round.startDate,
          });
      }
      // source === 'scheduler'
      return putRequest(`${apiUrl}v2/games/roundschedulers/${round.id}/`)
        .send({
          grading_weight: Number(round.gradingWeight) / 100,
          round_number: round.number,
          course: courseID,
          practice: false,
          start_date: round.startDate,
          end_date: round.endDate,
        });
    });

    Promise.all(requests)
      .then(() => {
        clearAppGeneralInfoCache();
        this.setState({
          showSuccess: true,
          successMessage: 'The round weights are updated.',
          roundsWeightsValueChanged: false,
        });
      })
      .catch(() => {
        this.setState({
          showError: true,
          errorTitle: 'Error | Update Failed',
          errorMessage: "Couldn't save all round weights. Please try again.",
        });
      });
  };

  hideError = () => {
    this.setState({ showError: false });
  };

  hideSuccess = () => {
    this.setState({ showSuccess: false });
  };

  render() {
    const {
      loaded,
      teamScore,
      peerScore,
      feedbackSubmission,
      engagementScore,
      scoreWeightsChanged,
      roundsWeights,
      roundsWeightsValueChanged,
      isDefaultRoundsActive,
      gameType,
      showError,
      errorTitle,
      errorMessage,
      showSuccess,
      successMessage,
    } = this.state;

    // Modify Round Weights section visibility:
    //   - Explorer game type: hidden (single-round game, no weighting)
    //   - All 3 main rounds missing a backing record: hidden
    //   - Otherwise: shown, but inputs for the specific missing
    //     rounds are disabled so instructors can only edit the
    //     rounds that actually exist on the BE.
    const allRoundsMissing = roundsWeights.every(r => r.source === 'default');
    const showRoundWeights = gameType !== 'Explorer' && !allRoundsMissing;

    if (!loaded) {
      return (
        <Wrapper>
          <Loader type="ball-scale-ripple" active />
        </Wrapper>
      );
    }

    const roundWeightsButtonText = isDefaultRoundsActive
      ? 'Activate'
      : roundsWeightsValueChanged
        ? 'Update'
        : 'Updated';

    const scoreWeightsButtonText = scoreWeightsChanged ? 'Update' : 'Updated';
    const isIAS = Auth.isAssistantOrCoInstructor();

    return (
      <Wrapper>
        {showError && (
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onHide={this.hideError}
          />
        )}
        {showSuccess && (
          <SuccessAlert
            title="Success!"
            message={successMessage}
            onHide={this.hideSuccess}
          />
        )}

        <Title>SCORING WEIGHTS</Title>
        <Text>
          Configure how scoring is calculated. Round weights drive how each round contributes to the cumulative team score. Score weights drive how the four activity scores combine into a student's individual score.
        </Text>

        {/* ---------- Round Weights -------------------------------- */}
        {/* Hidden for Explorer since Explorer runs a single main round
            (no weighting decision to make). */}
        {showRoundWeights && (
        <DataContainer>
          <DataContainerTitle>Modify Round Weights</DataContainerTitle>
          <Text>
            By default, each round is weighted equally toward the cumulative team score. Adjust the values below if you want a different weighting.
          </Text>
          <FourColumnsRow>
            {roundsWeights.map((round, index) => {
              // Disable the input when either:
              //   - the backing round has ended (locks closed rounds), or
              //   - no BE record exists for this round number
              //     (source = 'default'), so there's nothing to save
              //     against yet. Instructor can still see the slot but
              //     can only edit the rounds that are actually set up.
              let inputDisabled = round.source === 'default';
              if (!inputDisabled && round.endDate) {
                const endDate = getDateInCurrentTimezone(moment(round.endDate));
                const nowDate = getDateInCurrentTimezone(moment());
                inputDisabled = nowDate > endDate;
              }
              return (
                <DataColumn key={index}>
                  <DataTitle>{`Round ${round.number}`}</DataTitle>
                  <InputPercentage
                    value={round.gradingWeight}
                    name={round.number}
                    onInputChange={this.handleRoundWeightChange}
                    disabled={inputDisabled || isIAS}
                  />
                </DataColumn>
              );
            })}
          </FourColumnsRow>
          <ButtonRow>
            <Button
              type="button"
              onClick={this.submitRoundWeights}
              disabled={isIAS || !(roundsWeightsValueChanged || isDefaultRoundsActive)}
              isActiveDefault={!isIAS && isDefaultRoundsActive}
            >
              {roundWeightsButtonText}
            </Button>
          </ButtonRow>
        </DataContainer>
        )}

        {/* ---------- Score Weights -------------------------------- */}
        <DataContainer>
          <DataContainerTitle>Modify Score Weights</DataContainerTitle>
          <Text>
            How the four activity scores combine into a student's individual score: team performance, peer evaluations, peer feedback submission, and engagement. Sum must equal 100%.
          </Text>
          <FourColumnsRow>
            <DataColumn>
              <DataTitle>Team Score</DataTitle>
              <InputPercentage
                value={teamScore}
                name="teamScore"
                onInputChange={this.handleScoreWeightChange}
                disabled={isIAS}
              />
            </DataColumn>
            <DataColumn>
              <DataTitle>Peer Score</DataTitle>
              <InputPercentage
                value={peerScore}
                name="peerScore"
                onInputChange={this.handleScoreWeightChange}
                disabled={isIAS}
              />
            </DataColumn>
            <DataColumn>
              <DataTitle>Feedback Submission</DataTitle>
              <InputPercentage
                value={feedbackSubmission}
                name="feedbackSubmission"
                onInputChange={this.handleScoreWeightChange}
                disabled={isIAS}
              />
            </DataColumn>
            <DataColumn>
              <DataTitle>Engagement Score</DataTitle>
              <InputPercentage
                value={engagementScore}
                name="engagementScore"
                onInputChange={this.handleScoreWeightChange}
                disabled={isIAS}
              />
            </DataColumn>
          </FourColumnsRow>
          <ButtonRow>
            <Button
              type="button"
              onClick={this.submitScoreWeights}
              disabled={isIAS || !scoreWeightsChanged}
            >
              {scoreWeightsButtonText}
            </Button>
          </ButtonRow>
        </DataContainer>

        <Text>
          <strong>Note:</strong> The individual score is calculated once at the end of the simulation experience.
        </Text>
      </Wrapper>
    );
  }
}

export default OverallScore;



// WEBPACK FOOTER //
// ./src/instructor/components/grading/pages/overall-score.js